import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react'
import Compressor from 'compressorjs'
import { directUpload } from '@helpers/javascript/cable/directUpload'
import { MB, DEFAULT_UPLOAD_MAX_SIZE_MB } from '../../application/helpers/helpers'

export const Uploader = forwardRef(({
  children,
  onUpload,
  className,
  capture,
  style,
  max       = 4,
  maxSizeMb = DEFAULT_UPLOAD_MAX_SIZE_MB,
  multiple  = false,
  images    = false,
  onLoadingChange
}, ref) => {
  const initialState = {
    loading:          false,
    unprocessedFiles: [],
    processedFiles:   [],
    signedIds:        []
  }

  const fileInput         = useRef(null)
  const [state, setState] = useState(initialState)

  const reset      = () => {
    setState(initialState)
    if (fileInput.current) fileInput.current.value = ''
  }
  const askForFile = () => fileInput.current?.click()

  const onChange = () => {
    const files = Array.from(fileInput.current.files)
    processFiles(files)
  }

  const processFile = file => {
    file.type.startsWith("image/") ? compressImage(file) : uploadFile(file)
  }

  const processFiles = givenFiles => {
    const maxSize          = maxSizeMb * MB
    const unprocessedFiles = givenFiles.filter(file => file.size < maxSize)
    if (unprocessedFiles.length < givenFiles.length) {
      flashNotice(I18n.t("workorders.files.max_file_size", { max_size: maxSizeMb }))
    }

    if (unprocessedFiles.length > max) {
      flashNotice(I18n.t("mobile.files.too_many", { max }))
    } else if (unprocessedFiles.length > 0) {
      setState(prevState => ({ ...prevState, unprocessedFiles, loading: true}))
    }
  }

  const compressImage = image => {
    new Compressor(image, {
      quality: 0.6,
      maxWidth: 1024,
      maxHeight: 1024,
      success: uploadFile
    })
  }

  const uploadFile = file => {
    directUpload(file)
      .then(onUploaded)
      .catch(reset)
  }

  const onUploaded = signedId => setState(prevState => (
    {
      ...prevState,
      processedFiles:   [...prevState.processedFiles, prevState.unprocessedFiles.shift()],
      unprocessedFiles: [...prevState.unprocessedFiles],
      signedIds:        [...prevState.signedIds, signedId]
    }
  ))

  useEffect(() => {
    const file = state.unprocessedFiles[0]
    if (file) {
      processFile(file)
    } else if (state.signedIds.length > 0) {
      onUpload(state.signedIds, state.processedFiles)
      reset()
    }
  }, [state.unprocessedFiles])

  const onPaste = useCallback(e => {
    const items = e.clipboardData?.items
    if (!items) return

    const files = []
    for (let i = 0; i < items.length; i++) {
      const file = items[i].getAsFile?.()
      if (file) files.push(file)
    }

    if (files.length > 0) {
      processFiles(files)
      e.preventDefault()
    }
  }, [processFiles])

  const onDrop = useCallback(e => {
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      processFiles(files)
      e.preventDefault()
    }
  }, [processFiles])

  useImperativeHandle(ref, () => ({ onPaste, onDrop }))

  useEffect(() => { onLoadingChange?.(state.loading) }, [state.loading])

  return (
    <>
      {state.loading ? (
        <div className={className} style={style}>
          <div className="dot-elastic" />
        </div>
      ) : (
        <div
          onClick    = {askForFile}
          className  = {className}
          style      = {style}
          onDrop     = {onDrop}
          onDragOver = {e => e.preventDefault()}
        >
          {children}
        </div>
      )}

      <input
        type     = "file"
        accept   = {images ? "image/*" : "*"}
        onChange = {onChange}
        ref      = {fileInput}
        multiple = {multiple}
        capture  = {capture}
        hidden
      />
    </>
  )
})
